export const formatCurrency = (
  value: number | null | undefined,
  options: {
    nullAsEmpty?: boolean;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  } = {},
): string => {
  const {
    nullAsEmpty = false,
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
  } = options;

  if (value === null || value === undefined) {
    return nullAsEmpty ? "" : "0,00 €";
  }

  if (value === 0 && !nullAsEmpty) {
    return "0,00 €";
  }

  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(value);
};

export const formatNativePrice = (
  price: number,
  maximumFractionDigits: number,
): string =>
  new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits,
  }).format(price);

export const getNumberFormat = (amount?: number): string => {
  return amount !== undefined && amount !== null
    ? amount.toLocaleString(undefined, { minimumFractionDigits: 2 })
    : "0.00";
};

export const euroToNumber = (value: number) => {
  return value * 100;
};

export const numberToEuro = (value: number) => {
  return Number.parseFloat((value / 100).toFixed(2));
};

export const getCurrencySymbol = (currency: string) => {
  if (currency === "Dollar") {
    return "$";
  }
  return "€";
};

export const formatCompactNumber = (value: number): string => {
  if (Math.abs(value) > 999) {
    return new Intl.NumberFormat("en-US", {
      notation: "compact",
      compactDisplay: "short",
      maximumFractionDigits: 1,
    }).format(value);
  }
  return new Intl.NumberFormat("de-DE").format(value);
};

// Simple-interest amortization: may return a negative principal (payment < interest) and never validates principal against the outstanding balance — caller's responsibility.
export function calculatePrincipal(
  outstandingBalance: number,
  annualInterestRatePercent: number,
  monthlyPayment: number,
): number {
  const annualInterestRate = annualInterestRatePercent / 100;

  const interest =
    Math.round(outstandingBalance * (annualInterestRate / 12) * 100) / 100;

  const principal = monthlyPayment - interest;

  return principal;
}

export const formatPercent = (
  value: number | null,
  decimalPlaces: number = 1,
): string => {
  if (value === null) {
    return "—";
  }
  return `${value.toFixed(decimalPlaces)}%`;
};

export const formatSignedPercent = (value: number | null): string => {
  if (value === null) {
    return "—";
  }
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toFixed(1)}%`;
};

export const formatSignedCurrency = (value: number): string => {
  const amount = `${getNumberFormat(Math.abs(value))} €`;
  if (value > 0) {
    return `+${amount}`;
  }
  if (value < 0) {
    return `-${amount}`;
  }
  return amount;
};

export const formatDecimalAbsolute = (
  val: number,
  decimal: number = 1,
): string => {
  return Math.abs(val).toFixed(decimal);
};
